import { ensureError } from '@passionware/platform-js';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ChevronDown, ChevronRight, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SchemaError } from 'v4/entities/common/parseWithDecoratedError';
import { Alert, AlertDescription, AlertTitle } from 'v5/design-system/Alert';
import { Button } from 'v5/design-system/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from 'v5/design-system/Dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { cn } from 'v5/platform/dom/cn';
import { ZodError, ZodIssue } from 'zod';
import { TailwindRoot } from 'v4/shared/TailwindRoot';

function ExpandableButton({
  isExpanded,
  onToggle,
  children,
  className = ''
}: {
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        'flex items-center gap-1 w-full text-left py-0.5 px-1 rounded hover:bg-bg-subtle transition-colors',
        className
      )}
    >
      <ChevronRight
        className={cn(
          'w-3 h-3 mt-0.5 shrink-0 transition-transform',
          isExpanded && '[transform:rotate(90deg)]'
        )}
      />
      {children}
    </button>
  );
}

function ErrorCard({ issues }: { issues: ZodIssue[] }) {
  const hasMultipleErrors = issues.length > 1;

  return (
    <div className="ml-4 mt-1 mb-2 text-xs text-fg-danger bg-bg-danger border border-border-danger p-2.5 rounded-md shadow-sm">
      {issues.map((issue, idx) => (
        <div
          key={idx}
          className={cn(
            'flex items-start py-0.5',
            hasMultipleErrors ? 'gap-2' : ''
          )}
        >
          {hasMultipleErrors && (
            <span className="text-fg-danger mt-0.5 font-bold">•</span>
          )}
          <span className="leading-relaxed">{issue.message}</span>
        </div>
      ))}
    </div>
  );
}

function AnimatedContainer({
  isExpanded,
  children,
  className = ''
}: {
  isExpanded: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeInOut' }}
          className={cn('overflow-hidden', className)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export interface ErrorAlertProps {
  error: Error | unknown;
  title?: string;
  description?: string;
  className?: string;
}

interface ZodIssueMap {
  [path: string]: ZodIssue[];
}

/**
 * Maps Zod issues to their path keys for easy lookup
 */
function mapZodIssuesToPaths(issues: ZodIssue[]): ZodIssueMap {
  const map: ZodIssueMap = {};
  for (const issue of issues) {
    const path = issue.path.join('.');
    if (!map[path]) {
      map[path] = [];
    }
    map[path].push(issue);
  }
  return map;
}

/**
 * TreeNode component that manages its own expansion state
 */
function TreeNode({
  value,
  path,
  label,
  issueMap,
  initiallyExpanded,
  highlightedPath,
  onHighlightChange,
  isMissing = false
}: {
  value: unknown;
  path: string;
  label?: string | number;
  issueMap: ZodIssueMap;
  initiallyExpanded: boolean;
  highlightedPath?: string;
  onHighlightChange?: (path: string | undefined) => void;
  isMissing?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const issues = issueMap[path];
  const hasError = issues && issues.length > 0;
  const isHighlighted = highlightedPath === path;
  const nodeRef = useRef<HTMLDivElement>(null);

  // Auto-expand and scroll into view when highlighted
  useEffect(() => {
    if (isHighlighted && nodeRef.current) {
      // Expand this node
      setIsExpanded(true);

      // Scroll into view after a brief delay to allow expansion
      setTimeout(() => {
        nodeRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);

      // Clear highlight after 3 seconds
      setTimeout(() => {
        onHighlightChange?.(undefined);
      }, 3000);
    }
  }, [isHighlighted, onHighlightChange]);

  // Auto-expand when a descendant path is highlighted
  useEffect(() => {
    if (
      highlightedPath &&
      (path === '' || highlightedPath.startsWith(path + '.'))
    ) {
      setIsExpanded(true);
    }
  }, [highlightedPath, path]);

  // Render primitive values
  const renderPrimitive = () => {
    if (isMissing) {
      return <span className="text-fg-muted italic">(missing)</span>;
    }

    if (value === null) {
      return <span className="text-fg-muted italic">null</span>;
    }

    if (value === undefined) {
      return <span className="text-fg-muted italic">undefined</span>;
    }

    if (typeof value === 'string') {
      return <span className="text-fg-default">"{value}"</span>;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return <span className="text-fg-info">{String(value)}</span>;
    }

    return <span className="text-fg-muted">{String(value)}</span>;
  };

  // For primitive values, wrap in a div if we have highlighting capability
  if (typeof value !== 'object' || value === null) {
    if (isMissing) {
      // Missing fields render inline (matching regular field structure)
      return (
        <>
          <div
            ref={nodeRef}
            className={cn(
              'flex items-start gap-1 py-0.5 px-1 -ml-1 transition-all duration-300',
              isHighlighted && 'bg-bg-attention rounded px-1 -mx-1'
            )}
          >
            <span className="w-3 shrink-0" />
            <span
              className={cn(
                'font-medium',
                hasError ? 'text-fg-danger' : 'text-fg-default'
              )}
            >
              {label}:
            </span>{' '}
            {renderPrimitive()}
          </div>
          {hasError && <ErrorCard issues={issues} />}
        </>
      );
    }

    return (
      <div
        ref={nodeRef}
        className={cn(
          'transition-all duration-300',
          isHighlighted && 'bg-bg-attention rounded px-1 -mx-1'
        )}
      >
        {renderPrimitive()}
      </div>
    );
  }

  // Render arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div
          ref={nodeRef}
          className={cn(
            'transition-all duration-300',
            isHighlighted && 'bg-bg-attention rounded px-1 -mx-1'
          )}
        >
          <span className="text-fg-muted">[]</span>
        </div>
      );
    }

    const preview = <span className="text-fg-muted">[{value.length}]</span>;

    return (
      <div
        ref={nodeRef}
        className={cn(
          'transition-all duration-300',
          isHighlighted && 'bg-bg-attention rounded px-1 -mx-1'
        )}
      >
        {label !== undefined && (
          <ExpandableButton
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
          >
            <span className="text-fg-muted">{label}:</span>{' '}
            {!isExpanded && preview}
          </ExpandableButton>
        )}
        <AnimatedContainer isExpanded={isExpanded} className="ml-4">
          {value.map((item, index) => {
            const itemPath = `${path}.${index}`;
            const itemHasChildren =
              typeof item === 'object' &&
              item !== null &&
              (Array.isArray(item)
                ? item.length > 0
                : Object.keys(item).length > 0);

            return (
              <div key={index}>
                {itemHasChildren ? (
                  <TreeNode
                    value={item}
                    path={itemPath}
                    label={index}
                    issueMap={issueMap}
                    initiallyExpanded={
                      !!issueMap[itemPath] ||
                      Object.keys(issueMap).some(p =>
                        p.startsWith(`${itemPath}.`)
                      )
                    }
                    highlightedPath={highlightedPath}
                    onHighlightChange={onHighlightChange}
                    isMissing={false}
                  />
                ) : (
                  <div className="flex items-start gap-1 py-0.5 px-1 -ml-1">
                    <span className="w-3 shrink-0" />
                    <span className="text-fg-muted">{index}:</span>{' '}
                    <TreeNode
                      value={item}
                      path={itemPath}
                      issueMap={issueMap}
                      initiallyExpanded={false}
                      highlightedPath={highlightedPath}
                      onHighlightChange={onHighlightChange}
                      isMissing={false}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </AnimatedContainer>
      </div>
    );
  }

  // Render objects
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);

    // Find missing fields that have errors
    const missingFieldsWithErrors: string[] = [];
    Object.keys(issueMap).forEach(errorPath => {
      const pathPrefix = path ? `${path}.` : '';
      if (errorPath.startsWith(pathPrefix)) {
        const relativePath = errorPath.slice(pathPrefix.length);
        if (!relativePath.includes('.')) {
          const fieldName = relativePath;
          if (!entries.some(([k]) => k === fieldName)) {
            missingFieldsWithErrors.push(fieldName);
          }
        }
      }
    });

    const totalFields = entries.length + missingFieldsWithErrors.length;

    if (totalFields === 0) {
      return <span className="text-fg-muted">{'{}'}</span>;
    }

    const preview = (
      <span className="text-fg-muted">
        {'{'}
        {totalFields}
        {'}'}
      </span>
    );

    return (
      <div
        ref={nodeRef}
        className={cn(
          'transition-all duration-300',
          isHighlighted && 'bg-bg-attention rounded px-1 -mx-1'
        )}
      >
        {label !== undefined && (
          <ExpandableButton
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
          >
            <span
              className={cn(
                'font-medium',
                hasError ? 'text-fg-danger' : 'text-fg-default'
              )}
            >
              {label}:
            </span>{' '}
            {!isExpanded && preview}
          </ExpandableButton>
        )}
        {label !== undefined && hasError && <ErrorCard issues={issues} />}
        <AnimatedContainer isExpanded={isExpanded} className="ml-4">
          {entries.map(([k, v]) => {
            const childPath = path ? `${path}.${k}` : k;
            const childIssues = issueMap[childPath];
            const childHasError = childIssues && childIssues.length > 0;
            const childHasChildren =
              typeof v === 'object' &&
              v !== null &&
              (Array.isArray(v) ? v.length > 0 : Object.keys(v).length > 0);

            return (
              <div key={k}>
                {childHasChildren ? (
                  <TreeNode
                    value={v}
                    path={childPath}
                    label={k}
                    issueMap={issueMap}
                    initiallyExpanded={
                      !!issueMap[childPath] ||
                      Object.keys(issueMap).some(p =>
                        p.startsWith(`${childPath}.`)
                      )
                    }
                    highlightedPath={highlightedPath}
                    onHighlightChange={onHighlightChange}
                    isMissing={false}
                  />
                ) : (
                  <>
                    <div className="flex items-start gap-1 py-0.5 px-1 -ml-1">
                      <span className="w-3 shrink-0" />
                      <span
                        className={cn(
                          'font-medium',
                          childHasError ? 'text-fg-danger' : 'text-fg-default'
                        )}
                      >
                        {k}:
                      </span>{' '}
                      <TreeNode
                        value={v}
                        path={childPath}
                        issueMap={issueMap}
                        initiallyExpanded={false}
                        highlightedPath={highlightedPath}
                        onHighlightChange={onHighlightChange}
                        isMissing={false}
                      />
                    </div>
                    {childHasError && <ErrorCard issues={childIssues} />}
                  </>
                )}
              </div>
            );
          })}
          {missingFieldsWithErrors.map(fieldName => {
            const childPath = path ? `${path}.${fieldName}` : fieldName;

            return (
              <TreeNode
                key={`missing-${fieldName}`}
                value={undefined}
                path={childPath}
                label={fieldName}
                issueMap={issueMap}
                initiallyExpanded={false}
                highlightedPath={highlightedPath}
                onHighlightChange={onHighlightChange}
                isMissing={true}
              />
            );
          })}
        </AnimatedContainer>
      </div>
    );
  }

  return <span className="text-fg-muted">{String(value)}</span>;
}

/**
 * Renders a JSON tree with error annotations for ZodError
 */
function JsonTreeWithErrors({
  data,
  issueMap,
  path = '',
  highlightedPath,
  onHighlightChange
}: {
  data: unknown;
  issueMap: ZodIssueMap;
  path?: string;
  highlightedPath?: string;
  onHighlightChange?: (path: string | undefined) => void;
}) {
  // Check if root has errors
  const rootIssues = issueMap[path] || issueMap[''];

  return (
    <div className="font-mono text-sm">
      <TreeNode
        value={data}
        path={path}
        issueMap={issueMap}
        initiallyExpanded={true}
        highlightedPath={highlightedPath}
        onHighlightChange={onHighlightChange}
        isMissing={false}
      />
      {rootIssues && rootIssues.length > 0 && (
        <div className="mt-2 text-xs text-fg-danger bg-bg-danger/20 p-2 rounded">
          <div className="font-semibold mb-1">Root errors:</div>
          {rootIssues.map((issue, idx) => (
            <div key={idx}>{issue.message}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Displays a summary of validation issues with expandable details
 */
function ValidationIssuesSummary({
  issues,
  onIssueClick
}: {
  issues: ZodIssue[];
  onIssueClick?: (path: string) => void;
}) {
  const [issuesExpanded, setIssuesExpanded] = useState(false);

  return (
    <div className="bg-bg-attention border border-border-attention rounded-lg p-3 w-full">
      <div className="text-sm font-semibold text-fg-default mb-1">
        Validation Error
      </div>
      <Button
        variant="link"
        size="xs"
        className="text-fg-attention w-full justify-start"
        onClick={() => setIssuesExpanded(!issuesExpanded)}
      >
        {issuesExpanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        {issues.length} validation issue(s) found
      </Button>
      <AnimatePresence initial={false}>
        {issuesExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-border-attention">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border-attention">
                      <th className="text-left py-2 px-2 font-medium text-fg-default">
                        Path
                      </th>
                      <th className="text-left py-2 px-2 font-medium text-fg-default">
                        Error Message
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map((issue, idx) => {
                      const path =
                        issue.path.length > 0 ? issue.path.join('.') : '';
                      return (
                        <tr
                          key={idx}
                          className="border-b border-border-attention hover:bg-bg-attention transition-colors"
                        >
                          <td className="py-2 px-2">
                            <button
                              onClick={() => onIssueClick?.(path)}
                              className="text-left font-mono text-fg-attention hover:text-fg-default underline decoration-1 underline-offset-2 cursor-pointer transition-colors"
                            >
                              {path || 'root'}
                            </button>
                          </td>
                          <td className="py-2 px-2 text-fg-attention">
                            {issue.message}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export type ZodValidationDetailsBodyProps = {
  zodError: ZodError;
  /** Raw payload that failed validation (shown in the JSON tree). */
  data: unknown;
};

/**
 * Zod-focused validation UI (JSON tree + issue list) matching the dialog branch of {@link ErrorAlert}.
 */
export function ZodValidationDetailsBody({
  zodError,
  data
}: ZodValidationDetailsBodyProps) {
  const [highlightedPath, setHighlightedPath] = useState<string | undefined>();
  const issueMap = useMemo(
    () => mapZodIssuesToPaths(zodError.issues),
    [zodError]
  );

  return (
    <div className="flex max-h-[min(70vh,32rem)] min-w-0 flex-col gap-4 overflow-y-auto pr-1">
      <JsonTreeWithErrors
        data={data}
        issueMap={issueMap}
        highlightedPath={highlightedPath}
        onHighlightChange={setHighlightedPath}
      />
      <ValidationIssuesSummary
        issues={zodError.issues}
        onIssueClick={path => setHighlightedPath(path)}
      />
    </div>
  );
}

/**
 * Renders error details with special handling for SchemaError/ZodError
 */
function ErrorDetails({
  error,
  highlightedPath,
  onHighlightChange
}: {
  error: Error;
  highlightedPath?: string;
  onHighlightChange?: (path: string | undefined) => void;
}) {
  if (error instanceof SchemaError) {
    const zodError = error.innerError;
    const issueMap = mapZodIssuesToPaths(zodError.issues);

    return (
      <JsonTreeWithErrors
        data={error.data}
        issueMap={issueMap}
        highlightedPath={highlightedPath}
        onHighlightChange={onHighlightChange}
      />
    );
  }

  // Regular error
  return (
    <div className="space-y-2">
      <div>
        <h4 className="text-sm font-semibold text-fg-default mb-1">
          Error Details
        </h4>
        <div className="text-sm text-fg-muted bg-bg-inset rounded p-2 font-mono">
          {error.message}
        </div>
      </div>

      {error.stack && (
        <div>
          <h5 className="text-xs font-semibold text-fg-default mb-1">
            Stack Trace:
          </h5>
          <pre className="text-xs bg-bg-inset rounded p-2 overflow-auto max-h-48 text-fg-muted">
            {error.stack}
          </pre>
        </div>
      )}

      {error.cause !== undefined && error.cause !== null && (
        <div>
          <h5 className="text-xs font-semibold text-fg-default mb-1">Cause:</h5>
          <div className="text-xs bg-bg-inset rounded p-2">
            {error.cause instanceof Error
              ? error.cause.message
              : String(error.cause)}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Expandable text with line clamp
 */
function ExpandableText({
  children,
  className = ''
}: {
  children: string;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const needsExpansion = children.length > 150; // Rough estimate for 3 lines

  if (!needsExpansion) {
    return <span className={className}>{children}</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      <span className={cn(!expanded && 'line-clamp-3', className)}>
        {children}
      </span>
      <button
        onClick={e => {
          e.stopPropagation(); // Prevent triggering dialog
          setExpanded(!expanded);
        }}
        className="text-xs text-fg-default hover:underline flex items-center gap-1 self-start"
      >
        {expanded ? (
          <>
            <ChevronDown className="w-3 h-3" />
            Show less
          </>
        ) : (
          <>
            <ChevronRight className="w-3 h-3" />
            Show more
          </>
        )}
      </button>
    </div>
  );
}

/**
 * Expandable description with line clamp (wraps text in paragraph)
 */
function ExpandableDescription({ children }: { children: string }) {
  return (
    <p>
      <ExpandableText>{children}</ExpandableText>
    </p>
  );
}

/**
 * Generic error alert component with expandable details
 *
 * Features:
 * - Standard Alert component for error display
 * - "See details" opens a Dialog for SchemaError or Popover for regular errors
 * - Special handling for SchemaError (Zod validation errors):
 *   - Renders data as an interactive JSON tree
 *   - Anchors validation errors to their corresponding data paths
 *   - Highlights problematic fields in red
 * - For regular errors, shows message, stack trace, and cause
 *
 * @example
 * ```tsx
 * <ErrorAlert
 *   error={error}
 *   title="Failed to save"
 *   description="Unable to process your request"
 * />
 * ```
 */
export function ErrorAlert({
  error: rawError,
  title = 'Error',
  description,
  className
}: ErrorAlertProps) {
  const [open, setOpen] = useState(false);
  const error = ensureError(rawError);
  const isSchemaError = error instanceof SchemaError;

  const errorMessage = error.message || 'An unexpected error occurred';

  // For SchemaError, use Dialog for better viewing experience
  if (isSchemaError) {
    const schemaError = error as SchemaError;
    const issues = schemaError.innerError.issues;
    const issueCount = issues.length;

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Alert
            variant="destructive"
            className={cn(
              'cursor-pointer hover:bg-bg-danger/50 transition-colors',
              className
            )}
          >
            <AlertCircle />
            <AlertTitle>{title}</AlertTitle>
            <AlertDescription>
              <div className="space-y-2">
                {description && (
                  <ExpandableDescription>{description}</ExpandableDescription>
                )}
                <div>
                  <span className="text-sm leading-relaxed">
                    {issueCount === 1
                      ? 'Could not validate data received from server: 1 validation issue found.'
                      : `Could not validate data received from server: ${issueCount} validation issues found.`}
                  </span>
                  <span className="text-xs text-bg-danger-emphasis mt-1.5 block italic">
                    Click for details
                  </span>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </DialogTrigger>
        <DialogContent className="max-w-5xl max-h-[calc(100vh-1rem)]">
          <DialogHeader>
            <DialogTitle>Validation Error Details</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <ErrorDetails error={error} />
          </DialogBody>
          <DialogFooter className="p-4">
            <ValidationIssuesSummary issues={schemaError.innerError.issues} />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // For regular errors, use Popover (more lightweight)
  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <div className="space-y-2">
          {description && (
            <ExpandableDescription>{description}</ExpandableDescription>
          )}
          <div className="flex items-center gap-2">
            <ExpandableText className="text-sm flex-1">
              {errorMessage}
            </ExpandableText>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="tertiary"
                  size="xs"
                  className="h-6 text-xs shrink-0"
                >
                  See details
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[600px] max-w-[90vw]">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-fg-default">
                      Error Details
                    </h3>
                    <button
                      onClick={() => setOpen(false)}
                      className="text-fg-muted hover:text-fg-default"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <ErrorDetails error={error} />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}

export function renderError(error: unknown) {
  return <ErrorAlert error={error} />;
}

renderError.withClassName = (className: string) => (error: unknown) => (
  <ErrorAlert error={error} className={className} />
);

function withTailwindRoot(error: unknown) {
  return (
    <TailwindRoot>
      <ErrorAlert error={error} />
    </TailwindRoot>
  );
}
renderError.withTailwindRoot = withTailwindRoot;
withTailwindRoot.withClassName = (className: string) => (error: unknown) => (
  <TailwindRoot>
    <ErrorAlert error={error} className={className} />
  </TailwindRoot>
);

/**
 * Inline expandable error component designed for table/list contexts
 * Shows error details expanded inline without modals
 */
export function InlineErrorAlert({
  error: rawError,
  title = 'Unable to Load Data',
  description,
  className
}: ErrorAlertProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [highlightedPath, setHighlightedPath] = useState<string | undefined>();
  const error = ensureError(rawError);
  const isSchemaError = error instanceof SchemaError;

  const handleDoubleClick = () => {
    setIsExpanded(true);
  };

  const errorMessage = error.message || 'An unexpected error occurred';

  return (
    <div
      className={cn(
        'flex flex-col',
        'w-full max-h-full bg-linear-to-br from-bg-danger/10 to-bg-danger-emphasis/5 border border-border-danger rounded-lg p-6',
        className
      )}
      onDoubleClick={handleDoubleClick}
    >
      {/* Error Header */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0">
          <AlertCircle className="w-8 h-8 text-fg-danger" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-fg-danger mb-2">{title}</h3>
          {description && (
            <p className="text-fg-danger mb-3 leading-relaxed">{description}</p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-sm text-fg-danger font-medium">
              {isSchemaError
                ? `Data validation failed: ${(error as SchemaError).innerError.issues.length} issue(s) found`
                : errorMessage}
            </p>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="ml-4"
            >
              {isExpanded ? (
                <>
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Hide Details
                </>
              ) : (
                <>
                  <ChevronRight className="w-4 h-4 mr-2" />
                  Show Details
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Expandable Error Details */}
      <AnimatedContainer isExpanded={isExpanded} className="overflow-auto ">
        <div className="border-t border-bg-danger pt-4 isolate">
          {isSchemaError && (
            <div className="mb-4 sticky top-0 z-1">
              <ValidationIssuesSummary
                issues={(error as SchemaError).innerError.issues}
                onIssueClick={path => setHighlightedPath(path)}
              />
            </div>
          )}
          <ErrorDetails
            error={error}
            highlightedPath={highlightedPath}
            onHighlightChange={setHighlightedPath}
          />
        </div>
      </AnimatedContainer>
    </div>
  );
}

/**
 * Renders inline expandable error designed for table/list contexts
 */
export function renderInlineError(error: unknown) {
  return <InlineErrorAlert error={error} />;
}

renderInlineError.withClassName = (className: string) => (error: unknown) => (
  <InlineErrorAlert error={error} className={className} />
);
